import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import {
  UploadCloud, FileText, Image as ImageIcon, FileDown, Trash2, Share2,
  Lock, Copy, X, Loader2, Tag,
} from "lucide-react";

const CATEGORIES = [
  { id: "permit", label: "Permit" },
  { id: "warranty", label: "Warranty" },
  { id: "signoff", label: "Sign-off" },
  { id: "contract", label: "Contract" },
  { id: "insurance", label: "Insurance" },
  { id: "invoice_pdf", label: "Invoice PDF" },
  { id: "estimate_pdf", label: "Estimate PDF" },
  { id: "photo", label: "Photo" },
  { id: "other", label: "Other" },
];

const CAT_COLOR = {
  permit: "bg-blue-100 text-blue-700",
  warranty: "bg-purple-100 text-purple-700",
  signoff: "bg-emerald-100 text-emerald-700",
  contract: "bg-amber-100 text-amber-700",
  insurance: "bg-indigo-100 text-indigo-700",
  invoice_pdf: "bg-rose-100 text-rose-700",
  estimate_pdf: "bg-cyan-100 text-cyan-700",
  photo: "bg-pink-100 text-pink-700",
  other: "bg-slate-100 text-slate-700",
};

const humanSize = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const iconFor = (ct) => {
  if (ct?.startsWith("image/")) return ImageIcon;
  if (ct?.includes("pdf")) return FileText;
  return FileDown;
};

/**
 * <DocumentHub> — drop-in panel with drag-drop upload, category tagging,
 * lightbox preview for images, and per-doc share-link issuance.
 *
 * Props:
 *   userId? — bind docs to a client
 *   projectId? — bind docs to a project
 *   readOnly? — hide upload UI (used in client-side view of admin-uploaded docs)
 */
export default function DocumentHub({ userId, projectId, readOnly = false }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [filterCat, setFilterCat] = useState("");
  const [previewing, setPreviewing] = useState(null);
  const inputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (userId) params.user_id = userId;
      if (projectId) params.project_id = projectId;
      if (filterCat) params.category = filterCat;
      const r = await api.get("/documents", { params });
      setDocs(r.data);
    } catch (err) {
      console.warn("Documents fetch failed:", err);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [userId, projectId, filterCat]); // eslint-disable-line

  const handleFiles = async (files, category = "other") => {
    if (!files?.length) return;
    setUploading(true);
    let ok = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        if (userId) fd.append("user_id", userId);
        if (projectId) fd.append("project_id", projectId);
        fd.append("category", category);
        fd.append("title", file.name);
        await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok++;
      } catch (err) {
        console.error(`Upload failed for ${file.name}:`, err);
        toast.error(`${file.name}: ${err?.response?.data?.detail || "upload failed"}`);
      }
    }
    if (ok > 0) toast.success(`${ok} document${ok === 1 ? "" : "s"} uploaded`);
    setUploading(false);
    load();
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(Array.from(e.dataTransfer.files || []));
  };

  const remove = async (doc) => {
    if (!confirm(`Delete "${doc.title}"?`)) return;
    await api.delete(`/documents/${doc.document_id}`);
    toast.success("Deleted");
    load();
  };

  const share = async (doc) => {
    const r = await api.post(`/documents/${doc.document_id}/share`);
    const url = `${window.location.origin}${r.data.share_url}`;
    await navigator.clipboard.writeText(url);
    toast.success("Share link copied to clipboard (valid 30 days)");
  };

  return (
    <div className="space-y-4" data-testid="document-hub">
      {/* Upload + filter strip */}
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <Tag className="w-4 h-4 text-[#475569] dark:text-slate-400" />
          <button onClick={() => setFilterCat("")}
                  className={`text-xs px-2.5 py-1 rounded-full transition ${filterCat === "" ? "bg-[#0A2540] text-white" : "bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600"}`}
                  data-testid="doc-filter-all">All</button>
          {CATEGORIES.map((c) => (
            <button key={c.id} onClick={() => setFilterCat(c.id)}
                    className={`text-xs px-2.5 py-1 rounded-full transition ${filterCat === c.id ? "bg-[#0A2540] text-white" : "bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600"}`}
                    data-testid={`doc-filter-${c.id}`}>
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* Drop zone */}
      {!readOnly && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          data-testid="doc-dropzone"
          className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition ${
            dragOver ? "border-[#C9A356] bg-[#FFFBF0] dark:bg-amber-900/10" : "border-[#E2E8F0] dark:border-slate-700 hover:border-[#C9A356]"
          }`}
        >
          <input
            ref={inputRef} type="file" multiple
            accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
            className="hidden"
            onChange={(e) => handleFiles(Array.from(e.target.files || []))}
            data-testid="doc-file-input"
          />
          {uploading ? (
            <div className="text-sm text-[#475569] dark:text-slate-400 inline-flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Uploading…
            </div>
          ) : (
            <div>
              <UploadCloud className="w-8 h-8 text-[#C9A356] mx-auto mb-2" />
              <div className="font-medium text-[#0A2540] dark:text-white text-sm">Drop documents here</div>
              <div className="text-xs text-[#94A3B8] mt-1">or click to browse · PDFs, images, Office docs up to 16 MB</div>
            </div>
          )}
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="text-sm text-[#475569] dark:text-slate-400 py-4">Loading…</div>
      ) : docs.length === 0 ? (
        <div className="text-center py-8 text-sm text-[#94A3B8]">
          <FileText className="w-10 h-10 mx-auto opacity-30 mb-2" /> No documents yet
        </div>
      ) : (
        <ul className="space-y-2">
          {docs.map((d) => {
            const Icon = iconFor(d.content_type);
            return (
              <li key={d.document_id}
                  className="flex items-center gap-3 p-3 border border-[#E2E8F0] dark:border-slate-700 rounded-lg hover:border-[#C9A356] transition group"
                  data-testid={`doc-row-${d.document_id}`}>
                <div className="w-9 h-9 rounded-md bg-slate-100 dark:bg-slate-800 grid place-items-center shrink-0">
                  <Icon className="w-4 h-4 text-[#0A2540] dark:text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <button onClick={() => d.content_type?.startsWith("image/") ? setPreviewing(d) : window.open(d.url || "#", "_blank")}
                            className="text-sm font-medium text-[#0A2540] dark:text-white truncate hover:text-[#C9A356]"
                            data-testid={`doc-open-${d.document_id}`}>
                      {d.title}
                    </button>
                    <span className={`text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${CAT_COLOR[d.category] || CAT_COLOR.other}`}>
                      {d.category.replace(/_/g, " ")}
                    </span>
                  </div>
                  <div className="text-[11px] text-[#94A3B8] mt-0.5 truncate">
                    {humanSize(d.size)} · uploaded {new Date(d.created_at).toLocaleDateString()} by {d.uploaded_by_name || "—"}
                  </div>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                  <button onClick={() => share(d)} title="Share link" className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700" data-testid={`doc-share-${d.document_id}`}>
                    <Share2 className="w-4 h-4 text-[#475569] dark:text-slate-400" />
                  </button>
                  <a href={d.url} target="_blank" rel="noopener noreferrer" title="Download" className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700" data-testid={`doc-download-${d.document_id}`}>
                    <FileDown className="w-4 h-4 text-[#475569] dark:text-slate-400" />
                  </a>
                  {!readOnly && (
                    <button onClick={() => remove(d)} title="Delete" className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20" data-testid={`doc-del-${d.document_id}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Lightbox */}
      {previewing && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/80 p-6" onClick={() => setPreviewing(null)}>
          <button className="absolute top-4 right-4 text-white p-2" onClick={() => setPreviewing(null)}><X className="w-6 h-6" /></button>
          <img src={previewing.url} alt={previewing.title} className="max-h-[90vh] max-w-full rounded-md shadow-2xl" />
        </div>
      )}
    </div>
  );
}
